import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LogOut, MoonStar, SunMedium, UserRound } from "lucide-react";
import { cn } from "@weco/ui";
import { useSuiteChromeLayout } from "./SuiteChromeContext";
import { DARK_MODE_AVAILABLE } from "./initTheme";
import type { SuiteChromeUser } from "./types";

export interface UserAvatarProps {
  name: string;
  avatarUrl?: string | null;
  sizePx: number;
  className?: string;
  initialsClassName?: string;
}

function initialsFromName(name: string): string {
  if (!name.trim()) return "WC";
  return name
    .split(/\s+/)
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export const UserAvatar: React.FC<UserAvatarProps> = ({
  name,
  avatarUrl,
  sizePx,
  className = "",
  initialsClassName = "",
}) => {
  const [loaded, setLoaded] = useState(false);
  const trimmed = typeof avatarUrl === "string" ? avatarUrl.trim() : "";
  const showImage = trimmed.length > 0;

  const initials = useMemo(() => initialsFromName(name), [name]);

  const sizeStyle = { width: sizePx, height: sizePx, minWidth: sizePx, minHeight: sizePx };

  if (!showImage) {
    return (
      <div
        className={`flex items-center justify-center rounded-full text-[11px] font-bold shrink-0 overflow-hidden ${initialsClassName} ${className}`}
        style={{ ...sizeStyle, background: "var(--accent)", color: "var(--color-primary-foreground, #000)" }}
        aria-hidden
      >
        {initials}
      </div>
    );
  }

  return (
    <div className={`relative shrink-0 rounded-full overflow-hidden ${className}`} style={sizeStyle}>
      {!loaded && (
        <div
          className="absolute inset-0 animate-pulse rounded-full"
          style={{ background: "var(--bg-hover, #e5e5e5)" }}
          aria-hidden
        />
      )}
      <img
        src={trimmed}
        alt=""
        loading="lazy"
        referrerPolicy="no-referrer"
        className={`w-full h-full object-cover rounded-full transition-opacity duration-150 ${loaded ? "opacity-100" : "opacity-0"}`}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
      />
    </div>
  );
};

export interface UserMenuLabels {
  userMenuLabel?: string;
  viewProfileLabel?: string;
  languageLabel?: string;
  appearanceLabel?: string;
  toArabicLabel?: string;
  toEnglishLabel?: string;
  toDarkLabel?: string;
  toLightLabel?: string;
  logOutLabel?: string;
  loggingOutLabel?: string;
}

export interface UserMenuProps {
  user?: SuiteChromeUser | null;
  onSignOut?: () => void | Promise<void>;
  /** "View profile" row. Omitted when not provided. */
  onProfile?: () => void;
  labels?: UserMenuLabels;
  className?: string;
}

/**
 * The header avatar. The design sends the avatar straight to the profile
 * page, and moved theme + language off the header. The shell's profile page
 * carries neither a sign-out nor a language switch, so the avatar opens a
 * small menu instead: View profile first (the design's destination), then
 * language and appearance (so both stay reachable), then Log out.
 */
export const UserMenu: React.FC<UserMenuProps> = ({ user, onSignOut, onProfile, labels, className }) => {
  const {
    userMenuLabel = "User menu",
    viewProfileLabel = "View profile",
    languageLabel = "Language",
    appearanceLabel = "Appearance",
    toArabicLabel = "Switch to Arabic",
    toEnglishLabel = "Switch to English",
    toDarkLabel = "Switch to dark mode",
    toLightLabel = "Switch to light mode",
    logOutLabel = "Log out",
    loggingOutLabel = "Logging out…",
  } = labels ?? {};
  const { effectiveTheme, toggleTheme, locale, toggleLocale } = useSuiteChromeLayout();
  const isDark = effectiveTheme === "dark";
  const [open, setOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; start: number }>({ top: 0, start: 0 });

  // Anchor the menu under the avatar, on its inline-end edge (mirrors in RTL).
  useEffect(() => {
    if (!open) return;
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const rtl = document.documentElement.dir === "rtl";
    setPos({ top: r.bottom + 8, start: rtl ? r.left : window.innerWidth - r.right });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!btnRef.current?.contains(target) && !menuRef.current?.contains(target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    setOpen(false);
    try {
      await onSignOut?.();
    } finally {
      setLoggingOut(false);
    }
  };

  const rtl = typeof document !== "undefined" && document.documentElement.dir === "rtl";
  const pill = (code: "en" | "ar", text: string) => (
    <span
      aria-hidden="true"
      className={cn(
        "px-2 py-px rounded-md text-[10px] font-black tracking-[0.06em] leading-[18px] transition-all",
        locale === code ? "bg-primary text-primary-foreground" : "text-muted-foreground",
      )}
    >
      {text}
    </span>
  );

  const dropdown =
    open &&
    createPortal(
      <div
        ref={menuRef}
        role="menu"
        aria-label={userMenuLabel}
        style={{ position: "fixed", top: pos.top, [rtl ? "left" : "right"]: pos.start, zIndex: 9999 }}
        className="min-w-[240px] rounded-2xl border border-border bg-background shadow-2xl p-1.5"
      >
        <div className="px-3 py-2.5 mb-1 border-b border-border">
          <p className="text-sm font-semibold text-foreground truncate">{user?.name ?? "User"}</p>
          {user?.email && <p className="text-xs text-muted-foreground truncate">{user.email}</p>}
        </div>
        {onProfile && (
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onProfile();
            }}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm text-foreground hover:bg-muted transition-colors"
          >
            <UserRound className="size-4 text-muted-foreground" aria-hidden />
            {viewProfileLabel}
          </button>
        )}
        <div className="w-full flex items-center justify-between gap-3 px-3 py-1.5">
          <span className="text-sm text-foreground">{languageLabel}</span>
          <button
            type="button"
            role="menuitem"
            onClick={toggleLocale}
            title={locale === "en" ? toArabicLabel : toEnglishLabel}
            aria-label={locale === "en" ? toArabicLabel : toEnglishLabel}
            className="h-9 px-1 rounded-[14px] flex items-center gap-0.5 hover:bg-foreground/5 active:scale-95 transition-all"
          >
            {pill("en", "EN")}
            {pill("ar", "ع")}
          </button>
        </div>
        {DARK_MODE_AVAILABLE && (
          <div className="w-full flex items-center justify-between gap-3 px-3 py-1.5">
            <span className="text-sm text-foreground">{appearanceLabel}</span>
            <button
              type="button"
              role="menuitem"
              onClick={toggleTheme}
              title={isDark ? toLightLabel : toDarkLabel}
              aria-label={isDark ? toLightLabel : toDarkLabel}
              className="w-9 h-9 rounded-[14px] flex items-center justify-center text-foreground/70 hover:bg-foreground/5 hover:text-foreground active:scale-95 transition-all"
            >
              {isDark ? <SunMedium className="size-[18px]" aria-hidden /> : <MoonStar className="size-[18px]" aria-hidden />}
            </button>
          </div>
        )}
        <div className="my-1 border-t border-border" />
        <button
          type="button"
          role="menuitem"
          disabled={loggingOut}
          onClick={handleLogout}
          className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm text-foreground hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <LogOut className="size-4 text-muted-foreground" aria-hidden />
          {loggingOut ? loggingOutLabel : logOutLabel}
        </button>
      </div>,
      document.body,
    );

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        title={user?.name ?? userMenuLabel}
        aria-label={userMenuLabel}
        className={cn(
          "wc-hdr-avatar rounded-full ring-2 ring-transparent hover:ring-primary/30 active:scale-95 transition-all cursor-pointer flex items-center justify-center shrink-0 focus-visible:outline-none focus-visible:ring-primary",
          className,
        )}
      >
        <UserAvatar name={user?.name ?? "User"} avatarUrl={user?.avatarUrl} sizePx={30} />
      </button>
      {dropdown}
    </>
  );
};
